import React from 'react';
import { Text, Notice, Link } from '@heartinternet/hi-ui';
import type { ManageHostingPageProps } from './types';
import { PageMessages } from '../../components/PageMessages';
import { AssignHostingForm } from './components/AssignHostingForm';
import { AccessExtendSection } from './components/AccessExtendSection';
import './ManageHostingPage.css';

const DEFAULT_MESSAGES_PAGE_ID = 'page-manage-hosting';

/** Bespoke copy for known error codes; anything else → the generic message. */
const errorContent = (code: string): React.ReactNode => {
  if (code === 'ENOTFORFREE') {
    return (
      <>
        Free packages do not support domain references. Please{' '}
        <Link href="/manage/domain.cgi">purchase</Link> or{' '}
        <Link href="/manage/transfer.cgi">transfer</Link> a domain instead.
      </>
    );
  }
  return 'An unknown error occurred. Please try again later.';
};

/** Render up to three references (matching the legacy limit) as bold names, "and" before the last. */
const renderReferences = (domains: string[]): React.ReactNode => {
  const shown = domains.slice(0, 3);
  return shown.map((d, i) => (
    <React.Fragment key={d}>
      {i > 0 ? (i === shown.length - 1 ? ' and ' : ', ') : ''}
      <strong>{d}</strong>
    </React.Fragment>
  ));
};

/** Pre-filled multi-domain transfer-in URL (legacy uses `;` param separators). */
const transferUrl = (domains: string[]): string =>
  `/manage/transfer.cgi?submit=TRANSDOM;${domains
    .map((d) => `domain=${encodeURIComponent(d)};`)
    .join('')}no_ns_change=1`;

/**
 * Manage Web Hosting — the `/hosting` PATH_INFO mode of `/manage/manage.cgi`
 * (template `manage-hosting.tmpl`). Mirrors the existing CGI endpoints; no
 * backend migration.
 */
export const ManageHostingPage: React.FC<ManageHostingPageProps> = ({
  errors = [],
  newAccount = false,
  freeAccounts = [],
  showPark = false,
  parkedDomains = [],
  showManaged = false,
  hostAccounts = [],
  isReseller = false,
  transferableReferences = [],
  migrationPro2Shared = false,
  premiumMigrationEnabled = false,
  hasPremiumHosting = false,
  upgradeGrid,
  salesEmail,
  messagesPageId = DEFAULT_MESSAGES_PAGE_ID,
}) => (
  <div className="hi-ui-manage-hosting-page">
    {errors.map((code, i) => (
      <Notice key={`${code}-${i}`} variant="error" role="alert">
        <Text variant="body" inline>
          {errorContent(code)}
        </Text>
      </Notice>
    ))}

    <Text variant="heading-1">Manage web hosting</Text>

    <PageMessages pageId={messagesPageId} />

    {transferableReferences.length > 0 && (
      <Notice variant="info">
        <Text variant="body" inline>
          {renderReferences(transferableReferences)}{' '}
          {transferableReferences.length > 1 ? 'have' : 'has'} not been transferred to Heart
          Internet. Transfer them now and save on renewal fees.{' '}
          <Link href={transferUrl(transferableReferences)}>Transfer Now.</Link>
        </Text>
      </Notice>
    )}

    {newAccount && (
      <AssignHostingForm
        freeAccounts={freeAccounts}
        showPark={showPark}
        parkedDomains={parkedDomains}
      />
    )}

    <section className="hi-ui-manage-hosting-page__section">
      <Text variant="heading-2">Access web hosting control panel</Text>
      {showManaged ? (
        <AccessExtendSection
          hostAccounts={hostAccounts}
          isReseller={isReseller}
          migrationPro2Shared={migrationPro2Shared}
          premiumMigrationEnabled={premiumMigrationEnabled}
          hasPremiumHosting={hasPremiumHosting}
          upgradeGrid={upgradeGrid}
          salesEmail={salesEmail}
        />
      ) : (
        <Text variant="body">
          You do not have any web hosting accounts, you can order a hosting package from our{' '}
          <Link href="/manage/hosting.cgi">hosting page</Link>.
        </Text>
      )}
    </section>
  </div>
);
